import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertButton, AlertController, NavController } from '@ionic/angular';
import _ from 'lodash';

import {
  BOT_HANDLING,
  PROJECT_MEMBER_ROLES,
  REPLY_TIMEOUT_MINUTES_DEFAULT,
  routes,
  SUPPORT_STEP_KEYWORDS,
} from 'src/app/constants';
import { genericCompareFn } from 'src/app/utils/utils';
import { CustomerViewSettings, Organizer, SlackChannel } from 'src/models';
import { CHANNEL_TYPES, INTAKE_MODE_LABELS, INTAKE_MODES, IntakeSettings } from 'src/models/channels';
import { Project } from 'src/models/projects';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  selector: 'app-customer-admin-settings',
  templateUrl: './customer-admin-settings.page.html',
  styleUrls: ['./customer-admin-settings.page.scss'],
})
export class CustomerAdminSettingsPage implements OnInit {
  customer: Partial<Organizer>;
  project: Partial<Project>;
  customerId: string;
  isLoading = false;
  adminEmailField = '';
  slackChannelId = '';
  slackChannel?: SlackChannel;
  slackChannelName = '';
  slackChannelIsPrivate: boolean | null = null;
  slackChannelIsShared: boolean | null = null;
  slackTeamId = '';
  slackEnabled = false;
  syncSlackChannel = false;
  channel: any;
  supportSteps: any[] = [];
  replyTimeoutMinutes: number;
  showAdvanced = false;
  overrideTriage: boolean;
  defaultTriageChannelId: string;
  triageChannel?: SlackChannel;
  enableAutomatedTicketing: boolean;
  createdBy: string;
  botHandling?: string;
  autoresponder?: any;
  enabledAutomatedTicketing: boolean | null = null;
  assignToTaggedUserEnabled: boolean | null = null;
  assignToFirstResponderEnabled: boolean | null = null;
  isInteractiveMode: boolean | null = null;
  emailDomains: string[] = [];
  emailDomainToAdd: string;
  isInternal: boolean;
  supportStepRulesets: any[] = [];
  supportStepRulesetId?: string;
  customerViewEnabled = null;
  customerViewSettings?: CustomerViewSettings;
  conversationCreationConditions: any[] = [];
  conditionalConversationCreationEnabled = false;
  emailDomainTrackingEnabled: boolean;
  lookups: Record<string, Record<string, string>> = {};
  customerLoaded = false;
  intakeSettings: IntakeSettings;
  overrideDefaultCustomerViewMessage = false;
  overrideIntakeSettings = false;

  SUPPORT_STEP_KEYWORDS = SUPPORT_STEP_KEYWORDS;
  INTAKE_MODES = INTAKE_MODES;
  INTAKE_MODE_LABELS = INTAKE_MODE_LABELS;
  CHANNEL_TYPES = CHANNEL_TYPES;

  CONDITION_TYPES = {
    CONTAINS: 'contains',
    NOT_CONTAINS: 'notContains',
    EQUALS: '==',
    NOT_EQUALS: '!=',
    LIKE: 'like',
  };

  CONDITION_TYPE_LABELS = {
    [this.CONDITION_TYPES.CONTAINS]: 'contains',
    [this.CONDITION_TYPES.NOT_CONTAINS]: 'does not contain',
    [this.CONDITION_TYPES.EQUALS]: 'equals',
    [this.CONDITION_TYPES.NOT_EQUALS]: 'does not equal',
    [this.CONDITION_TYPES.LIKE]: 'contains',
  };

  CONDITIONS = {
    text: {
      label: 'Initial message text',
      types: [this.CONDITION_TYPES.LIKE],
    },
  };

  constructor(
    router: Router,
    public authService: AuthService,
    private navController: NavController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private slackService: SlackService,
    private customersService: CustomersService,
    private apiService: ApiService,
    private projectsService: ProjectsService,
  ) {
    this.customerId = router.url.split('/')[3];
  }

  async ngOnInit() {
    await this.loadSupportStepRulesets();

    this.getCustomer();
  }

  async loadSupportStepRulesets() {
    const supportStepRulesets: any = await this.apiService.postPromise(`/support-step-rulesets/list`, {});

    this.supportStepRulesets = supportStepRulesets.data;
  }

  async getCustomer() {
    const customer = await this.customersService.get(this.customerId);

    this.customer = customer;

    const user = this.authService.user;

    this.project = await this.projectsService.getById(this.customer.projectId);

    const projectMember = this.authService.projects.find((p) => p.id === this.customer?.projectId);

    if (
      !user ||
      ((this.customer.adminIds ?? []).indexOf(this.authService.userId) < 0 &&
        !this.authService.isTenantAdmin &&
        projectMember?.role !== PROJECT_MEMBER_ROLES.OWNER)
    ) {
      this.navController.navigateRoot(`/${routes.DASHBOARD}/home`);

      return;
    }

    if (this.customer.slackEnabled) {
      this.slackEnabled = this.customer.slackEnabled;
      this.slackChannelName = this.customer.slackChannel?.name;
      this.slackChannelId = this.customer.slackChannel?.id;
      this.slackChannelIsPrivate = this.customer.slackChannel?.isPrivate;
      this.slackChannelIsShared = this.customer.slackChannel?.isShared;
      this.slackTeamId = this.customer.slackChannel?.teamId;
    }

    this.syncSlackChannel = this.customer.syncSlackChannel ?? false;

    this.createdBy = this.customer.createdBy;
    this.supportStepRulesetId = this.customer.supportSteps?.length
      ? 'custom'
      : this.customer.supportStepRuleset
        ? this.customer.supportStepRuleset?.id
        : 'default';
    this.supportSteps = this.customer.supportSteps?.length
      ? this.customer.supportSteps
      : this.customer.supportStepRuleset
        ? this.customer.supportStepRuleset.rules
        : this.project.supportSteps;
    this.replyTimeoutMinutes = this.customer.replyTimeoutMinutes;
    this.defaultTriageChannelId = this.customer.defaultTriageChannelId;
    this.overrideTriage = !!this.defaultTriageChannelId;
    this.enableAutomatedTicketing =
      this.customer.disableAutomatedTicketing == null ? null : !this.customer.disableAutomatedTicketing;
    this.botHandling = this.customer.botHandling;
    this.intakeSettings = this.customer.intakeSettings;
    this.customerViewEnabled = this.customer.customerViewEnabled;
    this.customerViewSettings = this.customer.customerViewSettings ?? {};
    this.autoresponder = this.customer.autoresponder;
    this.assignToTaggedUserEnabled = this.customer.assignToTaggedUserEnabled;
    this.assignToFirstResponderEnabled = this.customer.assignToFirstResponderEnabled;
    this.emailDomains = [...(this.customer.emailDomains ?? this.customer.emailDomainsWrapper?.emailDomains ?? [])];
    this.isInternal = this.customer.isInternal;
    this.conversationCreationConditions = this.customer.conversationCreationConditions ?? [];
    this.conditionalConversationCreationEnabled = this.conversationCreationConditions.length > 0;
    this.emailDomainTrackingEnabled = !this.customer.emailDomainTrackingDisabled;
    this.overrideIntakeSettings = !!this.customer.intakeSettings;

    if (this.slackChannelId) {
      this.loadSlackChannel();
    }

    if (this.defaultTriageChannelId) {
      this.loadTriageChannel();
    }

    if (this.conversationCreationConditions.length) {
      await this.loadLookups();
    }

    this.customerLoaded = true;
  }

  async loadSlackChannel() {
    const slackChannel = await this.slackService.getChannel(this.slackChannelId, this.slackTeamId);

    if (slackChannel) {
      this.slackChannel = slackChannel;
    }
  }

  async loadTriageChannel() {
    const triageChannel = await this.slackService.getChannel(this.defaultTriageChannelId);

    if (triageChannel) {
      this.triageChannel = triageChannel;
    }
  }

  async save() {
    const user = this.authService.user;

    // In case they haven't finalized the domain they were typing
    this.addDomain();

    if (!user) {
      this.navController.navigateRoot('login');
      return;
    }

    if (!this.customer.name) {
      this.msgSrvc.show('Please add a customer name');
      return;
    }

    const updates: Partial<Organizer> = {
      slackChannelId: this.customer.slackChannelId,
    };

    if (this.customer.slackChannelId !== this.slackChannelId && this.slackChannel) {
      updates.slackChannelId = this.slackChannel?.id ?? null;
    }

    if (this.customer.slackTeamId !== this.slackTeamId && this.slackChannel) {
      updates.slackTeamId = this.slackChannel?.teamId ?? null;
    }

    if (updates.slackChannelId) {
      updates.slackEnabled = true;
    }

    if (_.isEqual(this.customer.emailDomainsWrapper?.emailDomains?.sort(), this.emailDomains?.sort())) {
      updates.emailDomains = null;
    } else {
      updates.emailDomains = this.emailDomains;
    }

    updates.supportSteps = this.supportStepRulesetId === 'custom' ? this.supportSteps : null;
    updates.supportStepRulesetId =
      this.supportStepRulesetId === 'custom' || this.supportStepRulesetId === 'default'
        ? null
        : this.supportStepRulesetId;
    updates.replyTimeoutMinutes = this.replyTimeoutMinutes;
    updates.defaultTriageChannelId = this.overrideTriage ? this.defaultTriageChannelId : null;
    updates.disableAutomatedTicketing = this.enableAutomatedTicketing == null ? null : !this.enableAutomatedTicketing;
    updates.intakeSettings = this.overrideIntakeSettings ? this.intakeSettings : null;
    updates.botHandling = this.botHandling;
    updates.customerViewEnabled = this.customerViewEnabled;
    updates.customerViewSettings = this.customerViewSettings;
    updates.autoresponder = this.autoresponder;
    updates.assignToTaggedUserEnabled = this.assignToTaggedUserEnabled;
    updates.assignToFirstResponderEnabled = this.assignToFirstResponderEnabled;
    updates.isInternal = this.isInternal;
    updates.conversationCreationConditions = this.conditionalConversationCreationEnabled
      ? this.conversationCreationConditions
      : [];
    updates.emailDomainTrackingDisabled = !this.emailDomainTrackingEnabled;

    this.isLoading = true;

    try {
      await this.customersService.update(this.customerId, updates);

      await this.getCustomer();

      this.isLoading = false;
      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      console.log(err);

      this.isLoading = false;
      this.msgSrvc.show('Something went wrong');
    }
  }

  async delete() {
    const subHeader = "We'll delete this customer, though conversations will still be visible in the dashboard";
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Do it',
          handler: async () => {
            try {
              this.isLoading = true;

              await this.customersService.delete(this.customerId);

              this.isLoading = false;

              this.navController.navigateRoot(`${routes.DASHBOARD}/customers`);
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  openSlack() {
    window.open(`https://slack.com/app_redirect?channel=${this.customer.slackChannelId}`);
  }

  supportStepsDidChange(supportSteps) {
    this.supportSteps = supportSteps;
  }

  onSelectSupportStepRulesetId(event) {
    const supportStepRulesetId = event.detail.value;

    if (!supportStepRulesetId) {
      return;
    }

    if (supportStepRulesetId === 'default') {
      this.supportStepRulesetId = supportStepRulesetId;
      this.supportSteps = this.project.supportSteps;
    } else if (supportStepRulesetId === 'custom') {
      this.supportStepRulesetId = supportStepRulesetId;
      this.supportSteps = this.customer.supportSteps ?? [];
    } else {
      const supportStepRuleset = this.supportStepRulesets.find((a) => a.id === supportStepRulesetId);

      if (supportStepRuleset) {
        this.supportStepRulesetId = supportStepRulesetId;
        this.supportSteps = supportStepRuleset.rules;
      }
    }
  }

  replyTimeoutMinutesCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  replyTimeoutMinutesDefaultLabel() {
    const tenantReplyTimeoutMinutes = this.authService.tenant.replyTimeoutMinutes ?? REPLY_TIMEOUT_MINUTES_DEFAULT;

    if (tenantReplyTimeoutMinutes === 0) {
      return 'Never';
    }

    if (tenantReplyTimeoutMinutes === -1) {
      return 'Always';
    }

    if (tenantReplyTimeoutMinutes < 60) {
      return `${tenantReplyTimeoutMinutes} minute${tenantReplyTimeoutMinutes === 1 ? '' : 's'}`;
    }

    const tenantReplyTimeoutHours = tenantReplyTimeoutMinutes / 60;

    return `${tenantReplyTimeoutHours} hour${tenantReplyTimeoutHours === 1 ? '' : 's'}`;
  }

  enableAutomatedTicketingCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  isInteractiveModeCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  enableAutomatedTicketingDefaultLabel() {
    const disableAutomatedTicketing = this.authService.tenant.disableAutomatedTicketing ?? false;

    if (disableAutomatedTicketing) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  genericCompareFn(v1, v2) {
    return genericCompareFn(v1, v2);
  }

  customerViewEnabledDefaultLabel() {
    const customerViewEnabled = this.authService.tenant.customerViewEnabled ?? false;

    if (!customerViewEnabled) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  intakeSettingsDefaultLabel() {
    return INTAKE_MODE_LABELS[
      this.authService.tenant?.defaultChannelIntakeSettings?.form?.enabled
        ? INTAKE_MODES.FORM
        : this.authService.tenant?.defaultChannelIntakeSettings?.bot?.enabled
          ? INTAKE_MODES.BOT_TRIAGE
          : INTAKE_MODES.IMMEDIATE
    ];
  }

  isCustomerViewPrivateDefaultLabel() {
    const isCustomerViewPrivate = this.authService.tenant.customerViewSettings?.isPrivate ?? false;

    return !isCustomerViewPrivate ? 'Disabled' : 'Enabled';
  }

  isCustomerViewPinnedToBottomDefaultLabel() {
    const isCustomerViewPinnedToBottom = this.authService.tenant.customerViewSettings?.isPinnedToBottom ?? false;

    return !isCustomerViewPinnedToBottom ? 'Disabled' : 'Enabled';
  }

  botHandlingCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  botHandlingDefaultLabel() {
    const tenantBotHandling = this.authService.tenant.botHandling ?? BOT_HANDLING.OFF;

    if (tenantBotHandling === BOT_HANDLING.OFF) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  assignToTaggedUserEnabledCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  assignToTaggedUserEnabledDefaultLabel() {
    const tenantAssignToTaggedUserEnabled = this.authService.tenant.assignToTaggedUserEnabled ?? true;

    if (tenantAssignToTaggedUserEnabled === false) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  assignToFirstResponderEnabledCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  assignToFirstResponderEnabledDefaultLabel() {
    const tenantAssignToFirstResponderEnabled = this.authService.tenant.assignToFirstResponderEnabled ?? true;

    if (tenantAssignToFirstResponderEnabled === false) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  onSlackChannelChanged(slackChannel?: SlackChannel) {
    this.slackChannel = slackChannel;

    this.customer.slackChannelId = slackChannel?.id ?? null;
    this.customer.slackEnabled = slackChannel?.id ? true : false;
    this.slackChannelId = slackChannel?.id ?? null;
    this.slackChannelName = slackChannel?.name ?? null;
    this.slackChannelIsPrivate = slackChannel?.isPrivate ?? false;
    this.slackChannelIsShared = slackChannel?.isShared ?? false;
    this.slackTeamId = slackChannel?.teamId ?? null;
    this.slackEnabled = slackChannel?.id ? true : false;

    if (slackChannel) {
      this.isInternal = !slackChannel.isShared;
    }

    this.save();
  }

  onTriageChannelChanged(slackChannel?: SlackChannel) {
    this.defaultTriageChannelId = slackChannel?.id ?? null;
    this.triageChannel = slackChannel;
  }

  addDomain() {
    if (!this.emailDomainToAdd || !this.emailDomainToAdd.includes('.')) {
      return;
    }

    if (!this.emailDomains) {
      this.emailDomains = [];
    }

    this.emailDomains.push(this.emailDomainToAdd.toLowerCase());
    this.emailDomainToAdd = '';
  }

  deleteDomain(index: number) {
    this.emailDomains.splice(index, 1);
  }

  async changeCustomerType() {
    const header = this.isInternal ? 'Internal channel' : 'Shared channel';
    const subHeader = this.isInternal
      ? 'This is an internal support channel for members of your Slack workspace.\n\nYou can change this to be a shared channel with external customers.'
      : 'This is an shared support channel for external customers or vendors.\n\nYou can change this to be an internal support channel for your own team.';

    const buttons: (string | AlertButton)[] = [
      {
        text: 'Dismiss',
      },
    ];

    if (this.isInternal) {
      buttons.push({
        text: 'Switch to External',
        handler: () => {
          this.isInternal = false;
          this.save();
        },
      });
    } else {
      buttons.push({
        text: 'Switch to Internal',
        handler: () => {
          this.isInternal = true;
          this.save();
        },
      });
    }

    const alert = await this.alertCtrl.create({
      header,
      subHeader,
      buttons,
    });
    alert.present();
  }

  supportStepsTrackBy(_i: number, supportStepRuleset: any) {
    return supportStepRuleset.id;
  }

  autoresponderDidChange(autoresponder: any) {
    this.autoresponder = autoresponder;
  }

  removeConditionalRule(i: number) {
    this.conversationCreationConditions.splice(i, 1);
  }

  onConditionChange(condition: any) {}

  conditionValues() {
    return Array.from(Object.entries(this.CONDITIONS));
  }

  showSearchOptions(condition: any) {}

  addConditionalRule() {
    const conversationCreationConditions = this.conversationCreationConditions ?? [];

    conversationCreationConditions.push({
      field: 'text',
      operator: 'like',
      value: '',
    });

    this.conversationCreationConditions = conversationCreationConditions;
  }

  async loadLookups() {
    const lookupIds: Record<string, string[]> = {};

    for (const conversationCreationCondition of this.conversationCreationConditions) {
      if (!lookupIds[conversationCreationCondition.field]) {
        lookupIds[conversationCreationCondition.field] = [];
      }

      lookupIds[conversationCreationCondition.field].push(conversationCreationCondition.value);
    }

    if (Object.keys(lookupIds).length) {
      this.lookups = await this.apiService.postPromise('/saved-filters/get-lookup-values', lookupIds);
    }
  }

  onConditionalConversationCreationChanged(event: any) {
    if (event.target.value === 'on' && !this.conversationCreationConditions.length) {
      this.addConditionalRule();
    }
  }

  onMessageContentChange(event) {
    this.customerViewSettings.customMessage = event;
  }

  onMessageOverrideChange() {
    if (!this.overrideDefaultCustomerViewMessage) {
      this.customerViewSettings.customMessage = null;
    }
  }

  onIntakeSettingsChange(config: IntakeSettings) {
    this.intakeSettings = config;
  }
}
